import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertCircle, ArrowLeft, Bookmark, BookOpen, CheckCircle, Library } from "lucide-react";
import { Section, Badge } from "@/components/ui/shared";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import JsonLd from "@/components/ui/JsonLd";
import ReadingProgress from "@/components/ui/ReadingProgress";
import { SkeletonDetailPage } from "@/components/ui/Skeleton";
import { ErrorState } from "@/components/ui/States";
import { useToast } from "@/components/ui/Toast";
import { MisconceptionEvidence, MisconceptionFeedback } from "@/components/clarifier/MisconceptionEvidence";
import { useSEO } from "@/hooks/useSEO";
import { useBookmarks } from "@/lib/bookmarks";
import { isNotFound, useMisconception, usePageView } from "@/lib/api";
import { misconceptionPath } from "@/lib/paths";
import { articleJsonLd, snippet } from "@/lib/seo";

/* ============================================
   Misconception Detail Page — /clarifier/:slug
   ============================================ */

export default function ClarifierDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: item, isLoading, isError, error, refetch } = useMisconception(slug);
  const { isBookmarked, toggle } = useBookmarks();
  const toast = useToast();

  const description = snippet(item?.shortAnswer || item?.summary);
  useSEO(item?.title ?? "Misconception", description, {
    type: "article",
    noindex: !isLoading && !item,
    path: item ? misconceptionPath(item) : undefined,
  });
  usePageView("clarifier", { id: item?.id, type: "misconception", title: item?.title });

  if (isLoading) {
    return (
      <main className="pt-16">
        <div className="bg-[var(--color-midnight)] py-16 md:py-24" />
        <Section><SkeletonDetailPage /></Section>
      </main>
    );
  }

  if (isError && !isNotFound(error)) {
    return (
      <main className="pt-16">
        <Section><ErrorState error={error} onRetry={refetch} /></Section>
      </main>
    );
  }

  if (!item) {
    return (
      <main className="pt-16">
        <Section>
          <div className="max-w-2xl mx-auto text-center py-20">
            <BookOpen className="w-16 h-16 text-[var(--color-cream-dark)] mx-auto mb-4" />
            <h1 className="font-[var(--font-display)] text-2xl font-bold text-[var(--color-midnight)] mb-2">Misconception Not Found</h1>
            <p className="text-[var(--color-ink-muted)] mb-6">This page doesn't exist or has been removed.</p>
            <Link to="/clarifier" className="btn-gold inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> All misconceptions
            </Link>
          </div>
        </Section>
      </main>
    );
  }

  const bmId = `clarifier-${item.id}`;
  const saved = isBookmarked(bmId);
  const handleBookmark = () => {
    const added = toggle({ id: bmId, type: "clarifier", title: item.claim, subtitle: item.category, route: misconceptionPath(item) });
    toast.success(added ? "Bookmarked!" : "Bookmark removed");
  };

  return (
    <main className="pt-16">
      <ReadingProgress />
      <JsonLd
        data={articleJsonLd({
          title: item.title,
          description,
          path: misconceptionPath(item),
          datePublished: item.createdAt,
          section: "Misconceptions about Islam",
        })}
      />

      {/* Hero */}
      <div className="bg-gradient-to-b from-rose-950 to-[var(--color-midnight)] py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 pattern-islamic opacity-20" />
        <div className="relative z-10 max-w-3xl mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center justify-between mb-6">
              <Link to="/clarifier" className="inline-flex items-center gap-2 text-[var(--color-cream)]/50 hover:text-[var(--color-cream)] text-sm transition-colors">
                <ArrowLeft className="w-4 h-4" /> All misconceptions
              </Link>
              <button onClick={handleBookmark} aria-pressed={saved} aria-label={saved ? "Remove bookmark" : "Bookmark"}
                className={`p-2 rounded-lg transition-colors ${saved ? "text-[var(--color-gold)] bg-[var(--color-gold)]/10" : "text-[var(--color-cream)]/40 hover:text-[var(--color-gold)] hover:bg-white/5"}`}>
                <Bookmark className={`w-5 h-5 ${saved ? "fill-current" : ""}`} />
              </button>
            </div>
            <Badge value={item.category} />
            <h1 className="mt-3 font-[var(--font-display)] text-3xl md:text-4xl font-bold text-[var(--color-cream)] leading-tight">
              {item.title}
            </h1>
            {item.titleArabic && (
              <p lang="ar" dir="rtl" className="font-arabic text-2xl text-[var(--color-gold)]/60 mt-3">{item.titleArabic}</p>
            )}
          </motion.div>
        </div>
      </div>

      <Section>
        <div className="max-w-3xl mx-auto">
          <Breadcrumbs crumbs={[{ label: "Misconceptions", to: "/clarifier" }, { label: item.title }]} />

          {/* The claim */}
          <div className="card-elevated p-5 md:p-6 mt-4">
            <h2 className="text-[10px] font-semibold uppercase tracking-wider text-red-600 mb-2 flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" /> The claim
            </h2>
            <p className="font-[var(--font-display)] text-lg text-[var(--color-midnight)]">"{item.claim}"</p>
            {item.objection && (
              <p className="text-sm text-[var(--color-ink-muted)] leading-relaxed mt-3">{item.objection}</p>
            )}
          </div>

          {/* Short answer */}
          <div className="card-elevated p-5 md:p-6 mt-4">
            <h2 className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600 mb-2 flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5" /> The short answer
            </h2>
            <p className="text-[var(--color-ink)] leading-relaxed">{item.shortAnswer || item.summary}</p>
          </div>

          {/* Evidence */}
          <div className="card-elevated px-5 pb-6 md:px-6 mt-4">
            <MisconceptionEvidence detail={item} />
            <MisconceptionFeedback item={item} />
          </div>

          {/* Further reading */}
          {item.furtherReading.length > 0 && (
            <div className="card-elevated p-5 md:p-6 mt-4">
              <h2 className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-ink-muted)] mb-3 flex items-center gap-1.5">
                <Library className="w-3.5 h-3.5" /> Further reading
              </h2>
              <ul className="space-y-2">
                {item.furtherReading.map((r, i) => (
                  <li key={i} className="text-sm text-[var(--color-ink)]">
                    {r.url ? (
                      <a href={r.url} target="_blank" rel="noreferrer" className="font-medium underline hover:text-[var(--color-gold)]">{r.title}</a>
                    ) : (
                      <span className="font-medium">{r.title}</span>
                    )}
                    {[r.author, r.publisher, r.year].filter(Boolean).length > 0 && (
                      <span className="text-[var(--color-ink-muted)]"> — {[r.author, r.publisher, r.year].filter(Boolean).join(", ")}</span>
                    )}
                    {r.note && <p className="text-xs text-[var(--color-ink-muted)] mt-0.5">{r.note}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Section>
    </main>
  );
}
